// One event as a supplier sees it (Lane 1331; new look Lane 1394, mockup v2).
//
// The header (title, buyer, round, due time in the viewer's zone with the zone
// named, and the Submit, Withdraw and Decline buttons the bid screen draws in
// it), then four tabs: Your quote (first: the bid screen, slice 2B), Documents
// and messages (the buyer's documents and a list of what the buyer did:
// round opened, feedback, changes), Event details (description and terms) and
// Your submissions. "How this works" opens the first time a person opens the
// event (remembered per event in this browser) and from a link in the header.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PortalShell from './PortalShell';
import { portalApi, PORTAL_HOME, newActionKey, howtoSeen, setHowtoSeen } from './portalApi';
import { AmendmentChanges, EventHeader, EventDetails, FeedbackBody, formatDueAt, formatUnitPrice } from './portalView';
import BidScreen from './BidScreen';
import { Banner, Modal } from './BidPanels';

const TABS = [['quote', 'Your quote'], ['docs', 'Documents and messages'], ['details', 'Event details'],
  ['history', 'Your submissions']];

// The award, as this supplier sees it (Lane 1395): its own parts and prices only.
function AwardSection({ award, currency }) {
  if (!award) return null;
  if (award.outcome === 'kept') {
    return <Banner>This event is closed. The parts you supply today stay with you for now; no new parts were awarded to you this time.</Banner>;
  }
  if (award.outcome !== 'won') return <Banner>This event is closed. You were not awarded parts this time. Thank you for taking part.</Banner>;
  const n = award.lines.length;
  return (
    <section className="srcp-card" data-testid="portal-award">
      <div className="srcp-card-h"><span className="srcp-card-t">You were awarded {n} {n === 1 ? 'part' : 'parts'}</span></div>
      <div className="srcp-card-b">
        <p className="srcp-mut">At the prices you quoted. The buyer will send purchase orders.</p>
        <div className="srcp-scroll">
        <table className="srcp-grid">
          <thead><tr><th>Part</th><th>Description</th>{award.lines.some((x) => x.site) && <th>Site</th>}
            <th className="srcp-r">Quantity</th><th className="srcp-r">Your price</th><th>Note</th></tr></thead>
          <tbody>
            {award.lines.map((x, i) => (
              <tr key={`${x.part_number}-${x.site || ''}-${i}`}>
                <td>{x.part_number}</td><td>{x.description || ''}</td>{award.lines.some((y) => y.site) && <td>{x.site || ''}</td>}
                <td className="srcp-r srcp-num">{Number(x.quantity).toLocaleString()}{x.unit ? ` ${x.unit}` : ''}</td>
                <td className="srcp-r srcp-num">{formatUnitPrice(x.price, currency)}</td>
                <td>{x.subject_to_qualification ? 'Subject to qualification' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </section>
  );
}

function formatDay(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function sizeText(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function typeText(contentType, fileName) {
  const t = String(contentType || '').toLowerCase();
  const n = String(fileName || '').toLowerCase();
  if (t.includes('pdf') || n.endsWith('.pdf')) return 'PDF';
  if (t.includes('sheet') || t.includes('excel') || /\.xlsx?$/.test(n) || n.endsWith('.csv')) return 'Excel';
  if (t.includes('word') || /\.docx?$/.test(n)) return 'Word';
  if (t.startsWith('image/')) return 'Image';
  return 'File';
}

function needs(a, action) {
  return (a.actions_needed || []).includes(action);
}

// What a change still asks of this supplier, each action on its own: an
// acknowledgement not given yet, and parts to quote again (the server keeps
// `quote_again` until those parts are quoted again, acknowledged or not).
function changeOutstanding(a) {
  return {
    acknowledge: !a.acknowledged && needs(a, 'acknowledge'),
    quoteAgain: needs(a, 'quote_again'),
  };
}

// A change that asks for something the supplier has not done yet.
function changeWaiting(a) {
  const o = changeOutstanding(a);
  return o.acknowledge || o.quoteAgain;
}

function changeStatus(a, canQuote) {
  const o = changeOutstanding(a);
  const quote = canQuote ? 'Quote again' : 'Prices no longer count';
  if (o.acknowledge && o.quoteAgain) {
    return ['srcp-pill--warn', canQuote ? 'Acknowledge, then quote again' : 'Acknowledge. Some prices no longer count'];
  }
  if (o.acknowledge) return ['srcp-pill--warn', 'Acknowledge to keep your prices'];
  if (o.quoteAgain) return ['srcp-pill--warn', a.acknowledged ? `Acknowledged. ${quote}` : quote];
  if (a.acknowledged) return ['', 'You acknowledged it'];
  return ['', 'Your prices still count'];
}

// The documents the buyer shares with suppliers (slice 2E-2).
function BuyerDocuments({ eventId, items, guide, onGuide }) {
  const [error, setError] = useState('');
  const download = async (d) => {
    setError('');
    try {
      const blob = await portalApi.document(eventId, d.document_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // The stored file's name when the buyer gave the document another name (Lane 1440).
      a.download = d.download_name || d.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      if (e.status !== 401) setError(e.message);
    }
  };
  return (
    <section className="srcp-card">
      <div className="srcp-card-h"><span className="srcp-card-t">Documents from the buyer</span></div>
      {error && <div className="srcp-card-b"><div className="srcp-error" role="alert">{error}</div></div>}
      {(!items || items.length === 0) && !guide && <div className="srcp-card-b srcp-mut">The buyer shared no documents.</div>}
      {((items && items.length > 0) || guide) && (
        <div className="srcp-scroll">
          <table className="srcp-grid">
            <thead>
              <tr><th>Name</th><th>Type</th><th className="srcp-r">Size</th><th>Added</th><th>Use</th><th><span className="srcp-sr">Download</span></th></tr>
            </thead>
            <tbody>
              {/* Lane 1440: Wingman's bid guide first; not a buyer document. */}
              {guide && (
                <tr data-testid="srcp-docs-guide">
                  <td className="srcp-strong srcp-ell" title={guide.title}>{guide.title}</td>
                  <td>PDF</td>
                  <td className="srcp-r srcp-num">{sizeText(guide.size_bytes)}</td>
                  <td>-</td>
                  <td>How to bid</td>
                  <td className="srcp-r">
                    <button type="button" className="srcp-btn srcp-btn--sm" onClick={onGuide}>Download</button>
                  </td>
                </tr>
              )}
              {(items || []).map((d) => (
                <tr key={d.document_id}>
                  <td className="srcp-strong srcp-ell" title={d.file_name}>{d.file_name}</td>
                  <td>{typeText(d.content_type, d.file_name)}</td>
                  <td className="srcp-r srcp-num">{sizeText(d.size_bytes)}</td>
                  <td>{formatDay(d.added_at)}</td>
                  <td>{d.informational ? 'For information only' : 'For quoting'}</td>
                  <td className="srcp-r">
                    <button type="button" className="srcp-btn srcp-btn--sm" onClick={() => download(d)}>Download</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// What the buyer did, newest first: the round opening, each feedback and each
// change. Feedback and changes open.
function messagesOf(event, roundOpenedAt) {
  const out = [];
  if (roundOpenedAt && event.round) {
    out.push({ key: 'round', at: roundOpenedAt,
      text: `Round ${event.round} is open${event.due_at ? `. Quotes are due ${formatDueAt(event.due_at)}` : ''}` });
  }
  (event.feedback || []).forEach((f, i) => out.push({
    key: `fb-${i}`, at: f.released_at, kind: 'feedback', item: f,
    text: `Feedback on your quote, round ${f.round}`,
  }));
  (event.amendments || []).forEach((a) => out.push({
    key: `ch-${a.amendment_id}`, at: a.published_at, kind: 'change', item: a,
    text: `The buyer changed this event (change ${a.number}, ${a.change_count} ${a.change_count === 1 ? 'item' : 'items'})`,
  }));
  return out.sort((x, y) => String(y.at || '').localeCompare(String(x.at || '')));
}

function MessagesList({ event, roundOpenedAt, openKey, onOpen, canQuote }) {
  const items = messagesOf(event, roundOpenedAt);
  return (
    <section className="srcp-card">
      <div className="srcp-card-h"><span className="srcp-card-t">From the buyer</span></div>
      {items.length === 0 && <div className="srcp-card-b srcp-mut">Nothing from the buyer yet.</div>}
      {items.length > 0 && (
        <div className="srcp-scroll">
          <table className="srcp-grid">
            <thead><tr><th>Date</th><th>What happened</th><th>What to do</th></tr></thead>
            <tbody>
              {items.map((m) => {
                const opens = Boolean(m.kind);
                const waiting = m.kind === 'change' && changeWaiting(m.item);
                const [tone, status] = m.kind === 'change' ? changeStatus(m.item, canQuote)
                  : (m.kind === 'feedback' ? ['', 'Open to read'] : ['', '']);
                const cls = [opens ? 'srcp-click' : '', openKey === m.key ? 'srcp-sel' : '', waiting ? 'srcp-unread' : '']
                  .filter(Boolean).join(' ');
                return (
                  <tr key={m.key} className={cls || undefined} onClick={opens ? () => onOpen(m) : undefined}>
                    <td>{formatDay(m.at)}</td>
                    <td className="srcp-ell srcp-ell--wide" title={m.text}>
                      {opens ? (
                        <button type="button" className="srcp-btn srcp-btn--link"
                          onClick={(e) => { e.stopPropagation(); onOpen(m); }}>{m.text}</button>
                      ) : m.text}
                    </td>
                    <td>{status && <span className={`srcp-pill${tone ? ` ${tone}` : ''}`}>{status}</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// One published change, its pages, and Acknowledge (slice 2E).
function ChangeWindow({ eventId, change, onClose, onAcknowledged }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const requestSeq = useRef(0);
  const show = useCallback((page = 1) => {
    const seq = ++requestSeq.current;
    setDetail(null);
    setError('');
    portalApi.amendment(eventId, change.amendment_id, page)
      .then((d) => { if (seq === requestSeq.current) setDetail(d); })
      .catch((e) => { if (seq === requestSeq.current && e.status !== 401) setError(e.message); });
  }, [eventId, change.amendment_id]);
  useEffect(() => { show(1); }, [show]);
  const acknowledge = async () => {
    setBusy(true);
    setError('');
    try {
      await portalApi.acknowledge(eventId, change.amendment_id, newActionKey());
      onAcknowledged();
    } catch (e) {
      if (e.status !== 401) setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const footer = (
    <>
      {change.acknowledged && <span className="srcp-pill srcp-pill--ok">You acknowledged this change</span>}
      {!change.acknowledged && needs(change, 'acknowledge') && (
        <button type="button" className="srcp-btn srcp-btn--primary" disabled={busy} onClick={acknowledge}>Acknowledge</button>
      )}
      <button type="button" className="srcp-btn" disabled={busy} onClick={onClose}>Close</button>
    </>
  );
  return (
    <Modal title={`Change ${change.number} from the buyer`} wide busy={busy} onClose={onClose} footer={footer}>
      <div className="srcp-mut">
        {change.published_at ? `Published ${formatDueAt(change.published_at)}. ` : ''}
        {change.change_count} {change.change_count === 1 ? 'item' : 'items'}.
      </div>
      {error && <div className="srcp-error" role="alert">{error}</div>}
      {!detail && !error && <div className="srcp-loading">Loading the changes…</div>}
      {detail && (
        <>
          <AmendmentChanges changes={detail.changes} />
          {detail.total_pages > 1 && (
            <div className="srcp-row">
              {detail.page > 1 && <button type="button" className="srcp-btn srcp-btn--sm" onClick={() => show(detail.page - 1)}>Previous</button>}
              <span className="srcp-mut">Page {detail.page} of {detail.total_pages}</span>
              {detail.page < detail.total_pages && <button type="button" className="srcp-btn srcp-btn--sm" onClick={() => show(detail.page + 1)}>Next</button>}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function HowThisWorks({ event, onClose }) {
  const round = event.round || 1;
  const due = formatDueAt(event.due_at);
  return (
    <Modal title="How this works" onClose={onClose}
      footer={<button type="button" className="srcp-btn srcp-btn--primary" onClick={onClose}>Got it</button>}>
      <ol className="srcp-howto">
        <li>
          Enter a Round {round} price for each part, or tick No bid.
          {round >= 2 ? ` Leave a price empty to keep your Round ${round - 1} price.` : ''}
        </li>
        <li>We save your draft as you type. Nothing reaches {event.buyer_name} until you press Submit quote.</li>
        <li>
          {due ? <>Submit before <span className="srcp-strong">{due}</span>. </> : ''}
          You can change your prices and submit again until the due time.
        </li>
      </ol>
      <div className="srcp-mut">You can also work in Excel: use Work in Excel above the parts.</div>
    </Modal>
  );
}

function EventView() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState('');
  const [ackTick, setAckTick] = useState(0);
  const [tab, setTab] = useState('quote');
  const [actionsEl, setActionsEl] = useState(null);
  const [resultsEl, setResultsEl] = useState(null);
  const [howto, setHowto] = useState(false);
  const [guideError, setGuideError] = useState('');
  const downloadGuide = async () => {
    setGuideError('');
    try {
      const blob = await portalApi.bidGuide(eventId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'How to submit your bid.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      if (e.status !== 401) setGuideError(e.message);
    }
  };
  const [roundOpenedAt, setRoundOpenedAt] = useState(null);
  const [openMsg, setOpenMsg] = useState(null);
  const [changeOpen, setChangeOpen] = useState(null);
  const [ackBusy, setAckBusy] = useState(false);
  const [ackError, setAckError] = useState('');
  const howtoChecked = useRef(false);

  // Also used after a submission, withdrawal or a refused save: it fetches the
  // event again without clearing the page, so the bid screen keeps its place.
  const reloadEvent = useCallback(() => {
    portalApi.event(eventId)
      .then(setEvent)
      .catch((e) => { if (e.status !== 401) setError(e.status === 404 ? 'not_found' : e.message); });
  }, [eventId]);

  useEffect(() => {
    setEvent(null);
    setError('');
    setTab('quote');
    setOpenMsg(null);
    howtoChecked.current = false;
    reloadEvent();
  }, [reloadEvent]);

  // The first time this person opens this event in this browser.
  useEffect(() => {
    if (!event || howtoChecked.current) return;
    howtoChecked.current = true;
    if (!howtoSeen(eventId)) setHowto(true);
  }, [event, eventId]);

  const closeHowto = () => { setHowtoSeen(eventId); setHowto(false); };

  const onAcknowledged = () => { setChangeOpen(null); reloadEvent(); setAckTick((n) => n + 1); };

  if (error === 'not_found') {
    return (
      <div className="srcp-empty">
        <div className="srcp-strong">Event not found</div>
        <Link className="srcp-textlink" to={PORTAL_HOME}>Back to your events</Link>
      </div>
    );
  }
  if (error) return <div className="srcp-error" role="alert">{error}</div>;
  if (!event) return <div className="srcp-loading">Loading event…</div>;

  const canQuote = Boolean(event.in_current_round && event.status === 'open');
  const amendments = event.amendments || [];
  const waiting = amendments.filter(changeWaiting);
  // The newest change still waiting on this supplier: the quote tab's banner.
  const pendingAmendment = waiting[0] || null;
  const acknowledgeNow = async () => {
    if (!pendingAmendment) return;
    setAckBusy(true);
    setAckError('');
    try {
      await portalApi.acknowledge(eventId, pendingAmendment.amendment_id, newActionKey());
      onAcknowledged();
    } catch (e) {
      if (e.status !== 401) setAckError(e.message);
    } finally {
      setAckBusy(false);
    }
  };
  const pendingChange = pendingAmendment ? {
    onReview: () => setChangeOpen(pendingAmendment),
    onAcknowledge: acknowledgeNow,
    canAcknowledge: changeOutstanding(pendingAmendment).acknowledge,
    busy: ackBusy,
  } : null;
  const openFeedback = openMsg && openMsg.kind === 'feedback' ? openMsg : null;

  return (
    <>
      <nav className="srcp-crumb" aria-label="Breadcrumb">
        <Link to={PORTAL_HOME}>Your events</Link><span aria-hidden="true">/</span><span>{event.buyer_name}</span>
      </nav>
      <EventHeader event={event}
        actions={<span ref={setActionsEl} className="srcp-head-actions" />}>
        <button type="button" className="srcp-btn srcp-btn--link" onClick={() => setHowto(true)}>How this works</button>
        {/* Lane 1440: the buyer's PDF guide to bidding (present while their switch is on). */}
        {event.bid_guide && (
          <button type="button" className="srcp-btn srcp-btn--link" onClick={downloadGuide}>How to bid</button>
        )}
      </EventHeader>
      {guideError && <div className="srcp-error" role="alert">{guideError}</div>}
      {event.status === 'cancelled' && <Banner>The buyer cancelled this event. Your prices are no longer used.</Banner>}
      {event.status === 'awarded' && <AwardSection award={event.award} currency={event.terms && event.terms.currency} />}
      {!event.in_current_round && event.status !== 'cancelled' && (
        <Banner>You are not part of the current round. You can see your earlier offers but cannot change them.</Banner>
      )}
      {ackError && <div className="srcp-error" role="alert">{ackError}</div>}
      <div ref={setResultsEl} className="srcp-results" />

      <div className="srcp-tabs" role="tablist" aria-label="Event">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key}
            className={`srcp-tab${tab === key ? ' srcp-tab--on' : ''}`}
            onClick={() => { setTab(key); setOpenMsg(null); }}>
            {label}
            {key === 'docs' && waiting.length > 0 && <span className="srcp-tab-cnt">{waiting.length}</span>}
          </button>
        ))}
      </div>

      {tab === 'docs' && (
        <>
          <BuyerDocuments eventId={eventId} items={event.documents} guide={event.bid_guide || null}
            onGuide={downloadGuide} />
          <MessagesList event={event} roundOpenedAt={roundOpenedAt} canQuote={canQuote}
            openKey={openMsg && openMsg.key}
            onOpen={(m) => {
              if (m.kind === 'change') setChangeOpen(m.item);
              else setOpenMsg(openMsg && openMsg.key === m.key ? null : m);
            }} />
          {openFeedback && (
            <section className="srcp-card">
              <div className="srcp-card-h">
                <span className="srcp-card-t">Feedback on your quote, round {openFeedback.item.round}</span>
                {openFeedback.item.released_at && <span className="srcp-mut">Sent {formatDueAt(openFeedback.item.released_at)}</span>}
                <span className="srcp-grow" />
                <button type="button" className="srcp-btn srcp-btn--sm" onClick={() => setOpenMsg(null)}>Close</button>
              </div>
              <div className="srcp-card-b">
                <FeedbackBody item={openFeedback.item} currency={event.terms && event.terms.currency} />
              </div>
            </section>
          )}
        </>
      )}

      {tab === 'details' && (
        <section className="srcp-card">
          <div className="srcp-card-b srcp-card-b--form"><EventDetails event={event} /></div>
        </section>
      )}

      {event.status === 'cancelled' && (tab === 'quote' || tab === 'history') && (
        <div className="srcp-mut">Quotes and submissions are closed for this event.</div>
      )}
      {event.status !== 'cancelled' && (
        <BidScreen key={eventId} event={event} eventId={eventId} onEventChanged={reloadEvent}
          refreshKey={ackTick} tab={tab} actionsEl={actionsEl} resultsEl={resultsEl}
          pendingChange={pendingChange}
          onDraft={(d) => setRoundOpenedAt(d.round_opened_at || null)} />
      )}

      {changeOpen && (
        <ChangeWindow eventId={eventId} change={changeOpen} onClose={() => setChangeOpen(null)}
          onAcknowledged={onAcknowledged} />
      )}
      {howto && <HowThisWorks event={event} onClose={closeHowto} />}
    </>
  );
}

export default function EventPage() {
  return <PortalShell><EventView /></PortalShell>;
}
